/** TanStack Query hooks for job management (SPA_TANSTACK_QUERY). */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/services/api";
import type { Job } from "@/types";

export function useSubmitJob() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useMutation<{ jobId: string }, Error, { pluginId: string; parameters: Record<string, unknown>; jobType?: string; name?: string }>({
    mutationFn: (body) => api.post("/reporting/jobs", body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      navigate(`/jobs/${data.jobId}`);
    },
  });
}

export function useJob(jobId: string) {
  return useQuery<Job>({
    queryKey: ["job", jobId],
    queryFn: () => api.get(`/reporting/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "complete" || status === "failed" || status === "completed_with_errors") return false;
      return 3000;
    },
  });
}

export function useJobs(filters?: Record<string, string>) {
  const params = filters ? new URLSearchParams(filters).toString() : "";
  const path = params ? `/reporting/jobs?${params}` : "/reporting/jobs";
  return useQuery<{ jobs: Job[] }>({
    queryKey: ["jobs", filters],
    queryFn: () => api.get(path),
  });
}

export function useJobResult(jobId: string, enabled = true) {
  return useQuery<{ content: string | Record<string, unknown>; format: string; downloadOnly?: boolean }>({
    queryKey: ["jobResult", jobId],
    queryFn: () => api.get(`/reporting/jobs/${jobId}/result`),
    enabled: !!jobId && enabled,
  });
}

export function useDownloadUrl(jobId: string) {
  return useQuery<{ url: string }>({
    queryKey: ["jobDownload", jobId],
    queryFn: () => api.get(`/reporting/jobs/${jobId}/download`),
    enabled: false, // Manual trigger via refetch
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (jobId) => api.del(`/reporting/jobs/${jobId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs"] }),
  });
}
